import { HelpArticle } from '../../types/help';

export const helpArticles: HelpArticle[] = [
  {
    id: 'getting-started',
    title: 'Getting Started with Approvn',
    category: 'Getting Started',
    keywords: ['introduction', 'overview', 'basics', 'start', 'begin'],
    route: '/',
    content: `
# Getting Started with Approvn

Approvn is a workflow automation and approval management system. Here's what you can do:

- **Create Workflows**: Define routing rules and approval levels  
  [Go to Workflows →](/workflows)
  
- **Track Orders**: Orders arrive from your connected systems and flow through your workflows  
  [View Submitted Orders →](/submitted-orders)
  
- **Manage Approvals**: Review and approve/reject pending items  
  [Action Required →](/action-required)
  
- **Configure Divisions**: Set up separate business units  
  [Configuration →](/configuration)

## Quick Start

1. Select your division from the top navigation
2. Navigate to [Workflows](/workflows) to view or create approval flows
3. Orders submitted by your connected systems will automatically route through your workflows
4. Monitor pending approvals on your [dashboard](/) or the [Action Required](/action-required) page

## Need Help?

- Press **⌘K** (Mac) or **Ctrl+K** (Windows) to quickly search help articles
- Click the **Help** button in the top navigation
- Look for help buttons on individual pages
    `
  },
  {
    id: 'workflow-creation',
    title: 'How to Create a Workflow',
    category: 'Workflows',
    keywords: ['create', 'workflow', 'new', 'routing', 'approval', 'setup', 'wizard'],
    route: '/workflows',
    relatedArticles: ['workflow-routing-rules', 'approval-levels', 'jsonpath-routing'],
    content: `
# Creating a Workflow

Workflows define how orders are routed and approved in your organization.

[Go to Workflows Page →](/workflows)

## Two Ways to Create a Workflow

### 🧙 Use Wizard (Recommended)
The wizard provides a step-by-step guided experience for creating complete workflows.

1. Navigate to **[Workflows](/workflows)** page
2. Select your division
3. Click **Use Wizard** button
4. Follow the wizard steps:
   - **Basic Information**: Name, description, and division
   - **Routing Rules**: Define conditions for order routing
   - **Approval Levels**: Set up approval hierarchy
   - **Review & Save**: Confirm your workflow configuration

### Quick Create
For experienced users, Quick Create provides a faster form-based workflow creation.

1. Click **Quick Create** button
2. Fill in all workflow details in one form
3. Save your workflow

## Workflow Components

**Routing Rules:**
- Define conditions that determine which workflow an order follows
- Use JSONPath expressions for complex routing logic
- Set priority order for rule evaluation

**Approval Levels:**
- Add one or more approval levels
- Assign approvers to each level
- Configure approval requirements (any approver, all approvers, etc.)

## Example
A purchase order workflow might have:
- Level 1: Manager approval (for orders < $1000)
- Level 2: Director approval (for orders > $1000)

[Go to Workflows →](/workflows)
    `
  },
  {
    id: 'workflow-routing-rules',
    title: 'Understanding Routing Rules',
    category: 'Workflows',
    keywords: ['routing', 'rules', 'conditions', 'logic', 'match'],
    route: '/workflows',
    relatedArticles: ['workflow-creation', 'jsonpath-routing'],
    content: `
# Workflow Routing Rules

Routing rules determine which workflow an order will follow based on its attributes.

## How Routing Works

1. When an order is submitted, the system evaluates routing rules in priority order
2. The first matching rule determines the workflow
3. If no rules match, the order uses the default workflow (if configured)

## Rule Components

- **Field**: Which order attribute to check (e.g., total amount, department)
- **Operator**: Comparison type (equals, greater than, contains, etc.)
- **Value**: The value to compare against
- **Priority**: Order of evaluation (lower numbers evaluated first)

## Best Practices

- Start with most specific rules (higher priority)
- End with catch-all rules (lower priority)
- Test rules thoroughly before activating
- Use meaningful names for debugging
    `
  },
  {
    id: 'jsonpath-routing',
    title: 'Using JSONPath in Routing Rules',
    category: 'Workflows',
    keywords: ['jsonpath', 'routing', 'expression', 'complex', 'advanced'],
    route: '/workflows',
    relatedArticles: ['workflow-routing-rules', 'workflow-creation'],
    content: `
# JSONPath Routing

JSONPath expressions allow complex routing logic based on order data structure.

## What is JSONPath?

JSONPath is a query language for JSON, similar to XPath for XML. It lets you extract values from nested order data.

## Common Patterns

- \`$.total\` - Access the total field
- \`$.items[0].price\` - Access first item's price
- \`$.customer.department\` - Access nested customer department
- \`$.items[?(@.category=='urgent')]\` - Filter items by category

## Examples

**Route based on line item category:**
\`\`\`
$.items[?(@.category=='capital')]
\`\`\`

**Route based on customer location:**
\`\`\`
$.customer.address.state
\`\`\`

**Route based on total of specific items:**
\`\`\`
$.items[?(@.type=='equipment')].sum(price)
\`\`\`

## Testing
Use the **[Workflow Simulator](/simulator)** to test sample order payloads and verify your JSONPath expressions and routing rules work correctly before connecting a live system.
    `
  },
  {
    id: 'approval-levels',
    title: 'Configuring Approval Levels',
    category: 'Approvals',
    keywords: ['approval', 'levels', 'approvers', 'sequential', 'parallel'],
    route: '/workflows',
    relatedArticles: ['workflow-creation', 'user-roles'],
    content: `
# Approval Levels

Approval levels define who needs to approve an order and in what sequence.

## Level Configuration

Each approval level has:
- **Name**: Descriptive name (e.g., "Manager Approval")
- **Approvers**: List of users who can approve at this level
- **Approval Mode**:
  - **Any Approver**: Only one assigned approver needs to approve
  - **All Approvers**: Everyone assigned must approve
  - **Specific Number of Approvals**: A configurable number of approvers must approve (e.g., 2 of 4)

## Sequential vs Parallel

- **Sequential**: Levels execute in order (Level 1, then Level 2, etc.)
- **Parallel**: Multiple approvers at the same level can approve simultaneously

## Bypassing Levels

You can configure rules to skip certain levels based on order attributes (e.g., skip manager approval for orders under $100).

## Dynamic Approvers

Approvers can be assigned dynamically based on:
- Order attributes (e.g., department from order)
- Routing rules
- User hierarchy
    `
  },
  {
    id: 'order-creation',
    title: 'How Orders Enter Approvn',
    category: 'Orders',
    keywords: ['create', 'order', 'submit', 'new', 'request', 'external', 'api', 'cxml', 'integration'],
    route: '/submitted-orders',
    relatedArticles: ['order-status', 'order-attributes'],
    content: `
# How Orders Enter Approvn

Orders are not created manually in Approvn. Instead, they are submitted automatically by the systems you connect to Approvn (e.g., procurement/ERP systems sending cXML, or other systems calling the external order submission API).

## How It Works

1. A connected system submits an order to Approvn's external order API, including the division, order details, line items, and any custom order attributes
2. Approvn evaluates routing rules to determine which workflow the order follows
3. The order is routed to the first approval level and approvers are notified
4. You can track the order's progress in-app the entire time

## Where to View Orders

- **[Submitted Orders](/submitted-orders)**: Orders you submitted that are awaiting routing/approval
- **[Action Required](/action-required)**: Orders waiting for your approval
- **[In Progress](/in-progress)**: Orders currently moving through approval levels
- **[Paused Orders](/paused-orders)**: Orders paused awaiting action
- **[Completed](/completed)**: Fully approved orders
- **[Cancelled](/cancelled)**: Cancelled or declined orders
- **[Error Queue](/error-queue)**: Orders that need resolution
- **[All Orders](/all-orders)**: Searchable report across every status and division

## Tips

- Work with your integration/IT team to ensure submitted orders include all fields your routing rules and approvers need
- Use [Custom Order Attributes](/workflows) to capture division-specific data from incoming orders
- Use the [Simulator](/simulator) to test how a sample order payload will route before connecting a live system
    `
  },
  {
    id: 'order-status',
    title: 'Understanding Order Status',
    category: 'Orders',
    keywords: ['status', 'order', 'pending', 'approved', 'rejected', 'tracking', 'submitted', 'processing', 'paused', 'completed', 'declined', 'error'],
    route: '/submitted-orders',
    relatedArticles: ['order-creation', 'pending-approvals'],
    content: `
# Order Status

Track where your order is in the approval process. Each status has its own page in the navigation.

## Status Types

- **Draft**: Order created but not yet submitted by the source system
- **Submitted**: Received by Approvn and awaiting routing - [Submitted Orders](/submitted-orders)
- **Pending / In Progress**: Awaiting approval at the current level - [Action Required](/action-required) (your approvals) or [In Progress](/in-progress) (all active orders)
- **Paused**: Temporarily on hold (e.g., waiting past a timeout) - [Paused Orders](/paused-orders)
- **Completed**: All required approvals finished - [Completed](/completed)
- **Partially Completed**: Some but not all approval levels finished
- **Declined**: Rejected by an approver - [Cancelled](/cancelled)
- **Cancelled**: Order withdrawn or cancelled
- **Needs Resolved**: Requires additional action before it can continue - [Error Queue](/error-queue)
- **Error**: Processing failed and needs attention - [Error Queue](/error-queue)

## Status Details

Click on any order to see:
- Current approval level
- Who has approved/rejected
- Approval history and timeline
- Comments from approvers
- Next approver(s)
- Full audit trail (every action taken on the order)

## Notifications

You'll receive email notifications when:
- Your order is approved/rejected
- Your order needs resolution
- Your order is at final approval

## Filtering

Each status page supports filtering and sorting (status, date range, search) to quickly find:
- Your pending orders
- Completed orders
- Orders needing your approval

Use **[All Orders](/all-orders)** to search across every status and division at once.
    `
  },
  {
    id: 'pending-approvals',
    title: 'Managing Pending Approvals',
    category: 'Approvals',
    keywords: ['pending', 'approvals', 'review', 'approve', 'reject', 'dashboard', 'filter', 'sort', 'pagination'],
    route: '/action-required',
    relatedArticles: ['order-status', 'approval-levels', 'approval-delegation'],
    content: `
# Managing Pending Approvals

Review and action items waiting for your approval.

[View Pending Approvals →](/action-required)

## Viewing Pending Approvals

1. Navigate to **[Action Required](/action-required)** page
2. You'll see all orders waiting for your approval
3. Use filters to narrow down:
   - By division
   - By workflow
   - By date range
   - By status
   - By search text
4. Sort and page through results - the list supports server-side sorting and pagination for large queues

## Reviewing an Order

Click on any pending order to:
- View full order details
- See approval history
- Check previous approver comments
- Review line items and attachments

## Approving/Rejecting

1. Review the order thoroughly
2. Add comments (optional but recommended)
3. Click **Approve** or **Reject**
4. Order moves to next level or back to requester

## Bulk Actions

You can approve/reject multiple orders at once:
1. Select checkboxes next to orders
2. Click bulk action button
3. Add comment for all selected
4. Confirm action

[Go to Approvals →](/action-required)

## Delegation

If you're unavailable, you can delegate your approvals to another user from the **[Delegations](/delegations)** page in the main navigation.
    `
  },
  {
    id: 'division-management',
    title: 'Managing Divisions',
    category: 'Divisions',
    keywords: ['division', 'business unit', 'organization', 'department', 'setup', 'configuration', 'timeout'],
    route: '/configuration',
    relatedArticles: ['user-roles'],
    content: `
# Division Management

Divisions allow you to separate workflows, users, and orders by business unit or department.

[Go to Configuration →](/configuration)

## What are Divisions?

Divisions provide complete isolation between different parts of your organization. Each division has its own:
- Workflows
- Orders
- Users and roles
- Default approval timeouts

## Creating a Division

1. Navigate to **[Configuration](/configuration)** (admin only)
2. Select the **Division Management** tab
3. Click **Create Division**
4. Enter:
   - **Name**: Division name
   - **Description**: Purpose of this division
   - **Default Step/Pause Timeouts**: Default hours before a step or paused order is escalated (can be overridden per workflow)
5. **Save**

## Assigning Users

1. Go to **Users** page
2. Select a user
3. Assign them to one or more divisions with specific roles (a user can have different roles in different divisions)

## Switching Divisions

Users with multiple division access can:
1. Click division selector in top navigation
2. Choose which division to work in
3. All views update to show that division's data

## Best Practices

- Create divisions for logical business separations
- Assign minimal necessary permissions
- Use consistent naming conventions
    `
  },
  {
    id: 'user-roles',
    title: 'Understanding User Roles',
    category: 'Users & Roles',
    keywords: ['roles', 'permissions', 'access', 'user', 'admin', 'approver', 'shopper', 'vendor', 'workflowadmin', 'divisionadmin'],
    route: '/users',
    relatedArticles: ['division-management'],
    content: `
# User Roles and Permissions

Roles determine what users can do, and are assigned per division - a user can hold different roles in different divisions.

## Available Roles

### Admin
- Full system access across all divisions
- Create/manage divisions
- Manage all users
- Organization-wide settings (SMTP, API keys, integrations)

### Division Admin
- Full control within their division(s)
- Create/edit/clone/delete workflows
- Manage division users and roles
- Configure division settings and default timeouts

### Workflow Admin
- Narrower, division-scoped role
- Create, edit, and clone workflows
- Cannot delete workflows or manage users (Admin/Division Admin only)

### Approver
- Approve/reject orders assigned to them
- View orders they're involved with

### Shopper
- The default role for people whose orders flow through Approvn
- View and track their own submitted orders

### Vendor
- Role for external vendor-facing access

## Multi-Division Roles

Users can have different roles in different divisions, for example:
- Division Admin in Division A
- Approver in Division B
- Shopper in Division C

A single user can also hold multiple roles within the same division.

## Permission Matrix

| Action | Admin | Division Admin | Workflow Admin | Approver | Shopper |
|--------|-------|-----------------|----------------|----------|---------|
| Create/Edit/Clone Workflow | ✓ | ✓ | ✓ | ✗ | ✗ |
| Delete Workflow | ✓ | ✓ | ✗ | ✗ | ✗ |
| Approve Order | ✓ | ✓ | ✗ | ✓ | ✗ |
| View All Division Orders | ✓ | ✓ | ✓ | ✗ | ✗ |
| Manage Users | ✓ | ✓ | ✗ | ✗ | ✗ |
| Organization Settings | ✓ | ✗ | ✗ | ✗ | ✗ |
    `
  },
  {
    id: 'email-notifications',
    title: 'Email Notifications',
    category: 'Settings',
    keywords: ['email', 'notification', 'smtp', 'alerts', 'configure', 'organization'],
    route: '/configuration',
    relatedArticles: ['configuration-settings', 'order-status'],
    content: `
# Email Notifications

Configure email notifications to keep users informed about order status changes.

## Notification Types

Users receive emails when:
- **Order Submitted**: Confirmation to order creator
- **Approval Needed**: Notification to approvers
- **Order Approved**: Update to order creator
- **Order Rejected**: Alert to order creator with reason
- **Order Needs Resolution**: Action required from creator

## SMTP Configuration

Admins configure email settings at the organization level:

1. Go to **[Configuration](/configuration)** > **Organization Settings** tab
2. Enter SMTP details:
   - SMTP Server
   - Port (usually 587 for TLS, 465 for SSL)
   - Username
   - Password (encrypted at rest)
   - From Email Address
   - From Name

3. Choose encryption: TLS or SSL
4. **Save Settings**

These SMTP settings apply across the whole organization (all divisions share one configuration).

## Notification Preferences

Users can control their notification preferences in their profile:
- Enable/disable specific notification types
- Email digest options (immediate, daily summary)
    `
  },
  {
    id: 'workflow-clone',
    title: 'Cloning Workflows',
    category: 'Workflows',
    keywords: ['clone', 'copy', 'duplicate', 'workflow', 'template'],
    route: '/workflows',
    relatedArticles: ['workflow-creation', 'workflow-routing-rules'],
    content: `
# Cloning Workflows

Quickly create new workflows based on existing ones.

## Why Clone?

- Create similar workflows with slight variations
- Use existing workflows as templates
- Set up workflows for new divisions
- Maintain version history

## How to Clone

1. Navigate to **[Workflows](/workflows)** page
2. Find the workflow you want to clone in the list
3. Click the **Clone** button in that workflow's row
4. Edit the cloned workflow:
   - Update name
   - Modify routing rules
   - Adjust approval levels
5. **Save** the new workflow

## What Gets Cloned

- Workflow structure
- Routing rules
- Approval levels
- Approver assignments (can be modified)

## What Doesn't Get Cloned

- Workflow ID (new one generated)
- Order history
- Active orders

## Best Practices

- Use clear naming to distinguish cloned workflows
- Review all settings before activating
- Test routing rules with sample orders
- Update approvers if cloning across divisions
    `
  },
  {
    id: 'pause-workflow',
    title: 'Pausing Workflows',
    category: 'Workflows',
    keywords: ['pause', 'disable', 'deactivate', 'workflow', 'stop'],
    route: '/workflows',
    relatedArticles: ['workflow-creation', 'order-status'],
    content: `
# Pausing Workflows

Temporarily disable a workflow without deleting it.

## When to Pause

- Workflow configuration needs updating
- Approval process is changing
- Holiday periods or system maintenance
- Approvers are unavailable
- Testing new workflows

## How to Pause

1. Go to **[Workflows](/workflows)** page
2. Click **Edit** on the workflow
3. On the **Basic Information** tab, uncheck the **Active** checkbox
4. **Save** the workflow

## What Happens When Paused

- No new orders can be routed to this workflow
- Existing orders continue through current approvals
- Workflow appears as "Inactive" in the list
- Routing rules won't match orders

## Resuming a Workflow

1. Edit the paused workflow
2. Re-check the **Active** checkbox
3. **Save** - the workflow is active again

## Impact on Orders

- **In-Progress Orders**: Continue normally
- **New Orders**: Will not be routed to an inactive workflow
- **Routing**: Falls through to next matching rule

## Pause Timeouts

Each division has default step/pause timeout settings (hours before a paused or stalled order is escalated). Individual workflows can override these defaults on the **Basic Information** tab.
    `
  },
  {
    id: 'order-attributes',
    title: 'Custom Order Attributes',
    category: 'Orders',
    keywords: ['attributes', 'custom fields', 'order', 'metadata', 'properties', 'jsonpath'],
    route: '/workflows',
    relatedArticles: ['order-creation', 'jsonpath-routing'],
    content: `
# Custom Order Attributes

Add custom fields to orders for division-specific needs.

## What are Attributes?

Attributes are custom fields that capture additional information about an order beyond the standard fields.

## Configuring Attributes

Attributes are configured per-workflow:

1. Go to **[Workflows](/workflows)** > open a workflow > **Edit**
2. Select the **Order Attributes** tab
3. Click **Add Attribute**
4. Define:
   - **Field Label**: Field name (e.g., "Department")
   - **JSON Path**: Where to find the value in the incoming order payload
   - **Type**: Text, number, date, dropdown, etc.
   - **Required**: Whether field must be present
   - **Validation**: Optional validation rules
5. **Save**

## Using Attributes

When an order arrives from a connected system:
- Approvn extracts each configured attribute from the order payload using its JSON Path
- Required attributes that are missing may cause the order to need resolution
- Attributes appear on the order detail page for approvers to review
- Attributes are available for use in routing rules

## Attribute Types

- **Text**: Single line of text
- **Text Area**: Multi-line text
- **Number**: Numeric values
- **Date**: Date picker
- **Dropdown**: Predefined options
- **Checkbox**: Yes/no values
- **Email**: Email validation
- **URL**: Web address

## Routing with Attributes

Use custom attributes in routing rules:
- Route by department
- Route by cost center
- Route by project code
- Any custom business logic

## Best Practices

- Keep attribute names clear and consistent
- Use dropdowns for standardized values
- Mark truly required fields only
- Document attribute purposes
    `
  },
  {
    id: 'troubleshooting-login',
    title: 'Troubleshooting Login Issues',
    category: 'Troubleshooting',
    keywords: ['login', 'authentication', 'okta', 'error', 'access', 'password'],
    route: '/login',
    relatedArticles: ['user-roles', 'division-management'],
    content: `
# Troubleshooting Login Issues

Common login problems and solutions.

## Cannot Log In

**Check:**
- Username/email is correct
- Password is correct and not expired
- Caps Lock is not on
- Account is active (not disabled)

**Solutions:**
- Use "Forgot Password" to reset
- Contact your admin to verify account status
- Clear browser cache and cookies
- Try a different browser

## Redirect Errors

If you get redirected to an error page:
1. Clear browser cache
2. Log out completely
3. Close all browser tabs
4. Open new browser window
5. Try logging in again

## OIDC/OAuth Errors

If using Okta or other SSO:
- Verify SSO configuration with admin
- Check that redirect URLs are correct
- Ensure you're using the correct login portal
- Contact IT support for SSO issues

## Account Access

If you can log in but can't access certain features:
- Check your assigned roles
- Verify division access
- Contact division admin for permissions

## Browser Compatibility

Approvn works best with:
- Chrome (latest)
- Firefox (latest)
- Safari (latest)
- Edge (latest)

## Still Having Issues?

Contact support with:
- Description of the problem
- Error messages (screenshot if possible)
- Browser and version
- Steps you've already tried
    `
  },
  {
    id: 'troubleshooting-orders',
    title: 'Troubleshooting Order Issues',
    category: 'Troubleshooting',
    keywords: ['order', 'error', 'stuck', 'workflow', 'routing', 'problem'],
    route: '/orders',
    relatedArticles: ['order-status', 'workflow-routing-rules', 'pending-approvals'],
    content: `
# Troubleshooting Order Issues

Common order-related problems and solutions.

## Order Stuck in Pending

**Possible causes:**
- Approver hasn't reviewed yet
- Approver is unavailable
- Email notifications not being delivered
- Workflow misconfigured

**Solutions:**
- Contact the assigned approver directly, or use [Delegations](/delegations) if they're unavailable
- Check the division's default step/pause timeout settings
- Verify SMTP configuration in [Configuration](/configuration)
- Admin/Division Admin can add additional approvers to an in-progress order

## Order Not Routing Correctly

**Check:**
- Routing rules in workflow (duplicate rules are blocked, but conflicting/overlapping rules can still cause unexpected matches)
- Rule priority order
- JSONPath expressions (if used)
- Order attributes match rule conditions

**Debug:**
1. View workflow routing rules
2. Check which rule should match
3. Verify order data matches rule
4. Use the **[Simulator](/simulator)** to test a sample order payload against the workflow

## Order Rejected at Submission

**Common issues:**
- Missing required order attributes
- Invalid data format from the source system
- Workflow is inactive
- No matching routing rules
- Division access/identifier mismatch

**Solutions:**
- Work with your integration team to fix the submitted payload
- Check order attribute validation messages on the order detail page
- Verify the workflow is active
- Contact admin about routing rules or division match identifiers

## Order Needs Resolution

This status means:
- Additional information required
- Conflict detected
- Clarification needed from creator

**Action required:**
1. View order details
2. Check resolution notes
3. Provide requested information
4. Resubmit order

## Approval Disappeared

If you can't find a pending approval:
- Check filter settings
- Verify division selection
- Order may have been approved by another approver
- Check **[All Orders](/all-orders)** view

## Email Notifications Not Received

**Check:**
- Spam/junk folder
- Email address in profile is correct
- Notification preferences enabled
- Organization SMTP configured correctly in **[Configuration](/configuration)** > Organization Settings

Contact admin to verify email configuration.
    `
  },
  {
    id: 'workflow-simulator',
    title: 'Testing Workflows with the Simulator',
    category: 'Workflows',
    keywords: ['simulator', 'test', 'diagnostics', 'sample order', 'dry run', 'routing test'],
    route: '/simulator',
    relatedArticles: ['workflow-routing-rules', 'jsonpath-routing', 'order-attributes'],
    content: `
# Testing Workflows with the Simulator

The Simulator lets you test how a sample order payload would route and which approvers would be notified - without submitting a real order.

[Go to Simulator \u2192](/simulator)

## How to Use It

1. Navigate to **[Simulator](/simulator)** and select a division
2. Paste or write a sample order JSON payload
3. Run the simulation
4. Review the results:
   - Which workflow and routing rule matched (or why none matched)
   - Which approval levels and approvers would be assigned
   - Any approvers that couldn't be resolved, with diagnostics explaining the mismatch

## Discovering Order Attributes

The Simulator can analyze your sample payload and suggest fields that aren't yet configured as custom order attributes. From the results you can:
- Create a new order attribute directly from a discovered field
- Add a new routing rule based on a discovered field

## When to Use It

- Before connecting a new system/integration, to confirm payloads route as expected
- After editing routing rules or approval levels, to confirm nothing broke
- When troubleshooting an order that routed unexpectedly
    `
  },
  {
    id: 'workflow-templates',
    title: 'Using Workflow Templates',
    category: 'Workflows',
    keywords: ['template', 'workflow templates', 'reuse', 'standardize'],
    route: '/workflow-templates',
    relatedArticles: ['workflow-creation', 'workflow-clone'],
    content: `
# Workflow Templates

Workflow Templates let you define reusable workflow structures (routing rules and approval levels) that can be applied when creating new workflows, rather than cloning an existing division's live workflow.

[Go to Workflow Templates \u2192](/workflow-templates)

## Managing Templates

1. Navigate to **[Workflow Templates](/workflow-templates)**
2. Create, edit, or delete templates (Admin/Division Admin can delete; Workflow Admin can edit)
3. Templates can be scoped to a division or kept generic for reuse across divisions

## Why Use Templates

- Standardize approval structures across similar divisions
- Speed up onboarding of new divisions
- Keep a clean starting point separate from in-use production workflows
    `
  },
  {
    id: 'approval-delegation',
    title: 'Delegating Your Approvals',
    category: 'Approvals',
    keywords: ['delegate', 'delegation', 'out of office', 'vacation', 'unavailable', 'backup approver'],
    route: '/delegations',
    relatedArticles: ['pending-approvals', 'approval-levels'],
    content: `
# Delegating Your Approvals

If you'll be unavailable, you can delegate your pending approvals to another user so orders don't stall.

[Go to Delegations \u2192](/delegations)

## Setting Up a Delegation

1. Navigate to **[Delegations](/delegations)** in the main navigation
2. Click to create a new delegation
3. Choose the user to delegate to and the division(s) it applies to
4. Optionally set a start and end date for the delegation
5. Save

## How It Works

- While active, orders awaiting your approval are also routed to your delegate
- Either you or your delegate can approve/reject the order
- You can end a delegation early at any time from the same page
    `
  },
  {
    id: 'configuration-settings',
    title: 'Organization and Division Configuration',
    category: 'Settings',
    keywords: ['configuration', 'organization settings', 'api key', 'integration', 'division management', 'timeout'],
    route: '/configuration',
    relatedArticles: ['email-notifications', 'division-management'],
    content: `
# Organization and Division Configuration

Admin-level settings live on the **[Configuration](/configuration)** page, split into two tabs.

## Organization Settings Tab

Applies to the entire organization (all divisions share this configuration):
- SMTP/email settings
- API keys for external integrations
- Authentication and integration settings
- Export/import organization settings as a backup (passwords are excluded from exports)

## Division Management Tab

Per-division configuration:
- Create, edit, and view divisions
- Manage division match identifiers (used to associate incoming orders/users with the correct division)
- Set default step and pause timeouts for the division (workflows can override these)
- Manage division-specific workflows

## Who Can Access This

Configuration requires the **Admin** or **Division Admin** role. Workflow Admins do not have access to the Configuration page.
    `
  },
  {
    id: 'order-audit-trail',
    title: 'Order Audit Trail and History',
    category: 'Orders',
    keywords: ['audit', 'history', 'activity log', 'print', 'trail'],
    route: '/all-orders',
    relatedArticles: ['order-status', 'order-creation'],
    content: `
# Order Audit Trail and History

Every order keeps a full activity log of everything that has happened to it.

## Viewing the Audit Trail

Open any order's detail page to see:
- Every status change, approval, rejection, and comment, in order
- Who performed each action and when
- Email notifications sent (flagged separately when sent to the submitter/shopper)
- Additional order details such as reference number, PO number, shipping method, department, and cost center (when provided by the source system)

## Printing

Use the print view on the order detail page to generate a clean, printable record of the order's full audit trail - useful for compliance or record-keeping.
    `
  },
  {
    id: 'reports',
    title: 'Reports: All Orders and Approver Activity',
    category: 'Orders',
    keywords: ['report', 'all orders', 'approver report', 'search', 'export'],
    route: '/all-orders',
    relatedArticles: ['order-status', 'order-audit-trail'],
    content: `
# Reports

## All Orders

**[All Orders](/all-orders)** is a searchable, filterable report across every status and division (depending on your access) - useful for finding a specific order when you don't know its current status.

## Workflow Approver Report

Admins can view **[Workflow Approver Report](/reports/workflow-approvers)** to see which users are configured as approvers across workflows - helpful for auditing who can approve what before making role changes.
    `
  }
];

export const getArticleById = (id: string): HelpArticle | undefined => {
  return helpArticles.find(article => article.id === id);
};

export const getArticlesByCategory = (category: string): HelpArticle[] => {
  return helpArticles.filter(article => article.category === category);
};

export const getRelatedArticles = (articleId: string): HelpArticle[] => {
  const article = getArticleById(articleId);
  if (!article || !article.relatedArticles) return [];
  
  return article.relatedArticles
    .map(id => getArticleById(id))
    .filter((a): a is HelpArticle => a !== undefined);
};
